/* eslint-disable react-hooks/rules-of-hooks */
import { resolveErrorMessage } from '@tebuto/functions/api'
import {
    buildBookingWidgetPaymentReturnUrl,
    buildStripeCheckoutSessionReturnUrl
} from '@tebuto/payment/stripe/resolve-stripe-return-url'
import { BookingContext } from '@tebuto/widget/booking/context'
import { isEventAlreadyBookedError } from '@tebuto/widget/booking/functions'
import {
    Booking,
    EventCategory,
    EventLocation,
    EventLocationSelection,
    MeetingProvider,
    OnlinePaymentMethods,
    ParsedBookableEvent,
    PaymentConfiguration,
    Therapist
} from '@tebuto/widget/booking/types'
import { DateTime } from 'luxon'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { BookingWidgetContext } from './context'
import type { WidgetSession } from './widget-auth/types'

interface PublicEventTherapist {
    id: number
    uuid: string
    name: string
    firstName: string | null
    lastName: string | null
    address: {
        streetAndNumber: string
        additionalInformation: string | null
        city: {
            name: string
            zip: string
        }
    } | null
}

interface PublicBookableEvent {
    title: string
    start: string
    end: string
    location: EventLocation
    color: string
    price: string
    taxRate: string
    onlineMeetingProvider?: MeetingProvider
    outageFeeEnabled: boolean
    outageFeeHours?: number
    outageFeePrice?: number
    eventRuleId: number
    eventCategoryId: number
    paymentEnabled: boolean
    paymentDuringBooking: boolean
    featurePaymentEnabled: boolean
    therapist: PublicEventTherapist
}

interface PublicApiCategory {
    id: number
    name: string
    description: string | null
    color: string
    price: string
    taxRate: string
    duration: number
    location: EventLocation
    outageFeeEnabled: boolean
    outageFeeHours?: number
    outageFeePrice?: number
    paymentEnabled: boolean
    paymentDuringBooking: boolean
}

interface PublicEventsApiResponse {
    events: PublicBookableEvent[]
    categories: PublicApiCategory[]
    bookingNote: string | null
}

function dedupeParsedBookableEvents(events: ParsedBookableEvent[]): ParsedBookableEvent[] {
    const seen = new Set<string>()
    const result: ParsedBookableEvent[] = []
    for (const event of events) {
        const therapistId = event.therapist?.id ?? 0
        const key = `${event.ruleId}|${event.start}|${event.categoryId}|${therapistId}`
        if (!seen.has(key)) {
            seen.add(key)
            result.push(event)
        }
    }
    return result
}

function parsePublicEventsResponse(
    body: PublicEventsApiResponse,
    setBookingNote: (note: string | null) => void
): {
    parsedEvents: ParsedBookableEvent[]
    categories: { [categoryId: number]: EventCategory }
    therapists: { [therapistId: number]: PublicEventTherapist }
} {
    const { events: apiEvents, categories: apiCategories } = body
    setBookingNote(body.bookingNote)

    const parsedEvents: ParsedBookableEvent[] = dedupeParsedBookableEvents(
        apiEvents.map((event) => ({
            ...event,
            customTitle: event.title,
            ruleId: event.eventRuleId,
            categoryId: event.eventCategoryId,
            location: event.location,
            onlineMeetingProvider: event.onlineMeetingProvider,
            startDatetime: DateTime.fromISO(event.start) as DateTime<true>,
            endDatetime: DateTime.fromISO(event.end) as DateTime<true>,
            therapist: event.therapist
        }))
    )

    const newCategories: { [categoryId: number]: EventCategory } = {}
    const newTherapists: { [therapistId: number]: PublicEventTherapist } = {}

    for (const cat of apiCategories) {
        newCategories[cat.id] = {
            id: cat.id,
            name: cat.name,
            description: cat.description,
            color: cat.color,
            price: cat.price,
            taxRate: cat.taxRate,
            duration: cat.duration,
            location: cat.location,
            outageFeeEnabled: cat.outageFeeEnabled,
            outageFeeHours: cat.outageFeeHours,
            outageFeePrice: cat.outageFeePrice,
            paymentEnabled: cat.paymentEnabled,
            paymentDuringBooking: cat.paymentDuringBooking
        }
    }

    apiEvents.forEach((event) => {
        if (!(event.eventCategoryId in newCategories)) {
            newCategories[event.eventCategoryId] = {
                id: event.eventCategoryId,
                name: event.title,
                color: event.color,
                price: event.price,
                taxRate: event.taxRate,
                location: event.location,
                onlineMeetingProvider: event.onlineMeetingProvider,
                outageFeeEnabled: event.outageFeeEnabled,
                outageFeeHours: event.outageFeeHours,
                outageFeePrice: event.outageFeePrice,
                paymentEnabled: event.paymentEnabled,
                paymentDuringBooking: event.paymentDuringBooking
            }
        }
        if (event.therapist && !(event.therapist.id in newTherapists)) {
            newTherapists[event.therapist.id] = event.therapist
        }
    })

    return { parsedEvents, categories: newCategories, therapists: newTherapists }
}

export interface GetBookingWidgetHooksOptions {
    session?: WidgetSession | null
}

export function getBookingWidgetHooks(apiUrl: string, options: GetBookingWidgetHooksOptions = {}) {
    const session = options.session ?? null
    function useParallelWidgetBootstrap(
        uuid: string,
        filterForCategories: number[] | undefined,
        includeSubusers: boolean,
        fingerprint: string
    ) {
        const [therapist, setTherapist] = useState<Therapist>()
        const [prefetchedPublicEvents, setPrefetchedPublicEvents] = useState<PublicEventsApiResponse | null>(null)
        const [showLoading, setShowLoading] = useState(true)
        const [error, setError] = useState<string | null>(null)
        const loadSeq = useRef(0)

        const load = useCallback(async () => {
            const seq = ++loadSeq.current
            setShowLoading(true)
            setError(null)
            let eventsUrl = `${apiUrl}/events/${uuid}?fingerprint=${fingerprint}`
            if (filterForCategories) {
                eventsUrl += `&categories=${filterForCategories.join(',')}`
            }
            if (includeSubusers) {
                eventsUrl += '&includeSubusers=true'
            }

            const [therapistRes, eventsRes] = await Promise.all([
                fetch(`${apiUrl}/therapists/uuid/${uuid}`),
                fetch(eventsUrl)
            ])

            const therapistBody = await therapistRes.json()
            const eventsBody = await eventsRes.json()

            if (seq !== loadSeq.current) {
                return
            }

            setShowLoading(false)

            if (!therapistRes.ok) {
                setError(resolveErrorMessage(therapistBody))
                setTherapist(undefined)
                setPrefetchedPublicEvents(null)
                return
            }

            if (!eventsRes.ok) {
                setError(resolveErrorMessage(eventsBody))
                setTherapist({ ...therapistBody })
                setPrefetchedPublicEvents(null)
                return
            }

            setTherapist({ ...therapistBody })
            setPrefetchedPublicEvents(eventsBody as PublicEventsApiResponse)
        }, [apiUrl, uuid, filterForCategories, includeSubusers, fingerprint])

        useEffect(() => {
            void load()
        }, [load])

        return { therapist, prefetchedPublicEvents, showLoading, error, mutate: load }
    }

    function useBookableEvents(
        filterForCategories: number[] | undefined,
        includeSubusers = false,
        prefetchedPublicEvents: PublicEventsApiResponse | null,
        fingerprint: string
    ) {
        return () => {
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern - hook is called at component top level
            const { therapistUUID, setBookingNote } = useContext(BookingWidgetContext)

            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const fetched = useRef(false)
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const isFirstFilterEffect = useRef(true)
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const [events, setEvents] = useState<ParsedBookableEvent[]>([])
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const [categories, setCategories] = useState<{ [categoryId: number]: EventCategory }>([])
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const [therapists, setTherapists] = useState<{ [therapistId: number]: PublicEventTherapist }>({})
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const [showLoading, setShowLoading] = useState(prefetchedPublicEvents == null)
            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            const [error, setError] = useState<string | null>(null)

            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            useEffect(() => {
                if (prefetchedPublicEvents) {
                    const parsed = parsePublicEventsResponse(prefetchedPublicEvents, setBookingNote)
                    setEvents(parsed.parsedEvents)
                    setCategories(parsed.categories)
                    setTherapists(parsed.therapists)
                    setShowLoading(false)
                    setError(null)
                    fetched.current = true
                    return
                }
                if (!fetched.current) {
                    fetched.current = true
                    fetchEvents()
                }
            }, [prefetchedPublicEvents])

            // biome-ignore lint/correctness/useHookAtTopLevel: Hook factory pattern
            useEffect(() => {
                if (isFirstFilterEffect.current) {
                    isFirstFilterEffect.current = false
                    return
                }
                if (prefetchedPublicEvents) {
                    return
                }
                if (fetched.current) {
                    fetchEvents()
                }
            }, [filterForCategories, includeSubusers, prefetchedPublicEvents])

            async function fetchEvents() {
                let url = `${apiUrl}/events/${therapistUUID}?fingerprint=${fingerprint}`
                if (filterForCategories) {
                    url += `&categories=${filterForCategories.join(',')}`
                }
                if (includeSubusers) {
                    url += '&includeSubusers=true'
                }

                const response = await fetch(url)
                setShowLoading(false)

                const body = await response.json()
                if (!response.ok) {
                    setError(resolveErrorMessage(body))
                    return
                }

                const parsed = parsePublicEventsResponse(body as PublicEventsApiResponse, setBookingNote)
                setCategories(parsed.categories)
                setTherapists(parsed.therapists)
                setEvents(parsed.parsedEvents)
            }

            return { events, categories, therapists, showLoading, error, mutate: fetchEvents }
        }
    }

    // Mutations

    function useCheckBookingAvailability() {
        const { therapistUUID, setRequirePhoneNumber, setRequireAddress, setRequireBirthdate } =
            useContext(BookingWidgetContext)
        const fingerprint = useFingerprint()

        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)

        async function checkBookingAvailability({
            event,
            onSuccess
        }: {
            event: ParsedBookableEvent
            onSuccess: (response: { isAvailable: boolean }) => void
        }) {
            setIsLoading(true)

            const response = await fetch(`${apiUrl}/events/${therapistUUID}/claim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    fingerprint,
                    start: event.start,
                    end: event.end,
                    eventRuleId: event.ruleId,
                    eventCategoryId: event.categoryId
                })
            })

            const body = await response.json()

            if (!response.ok) {
                setError(resolveErrorMessage(body))
                setIsLoading(false)
                return
            }

            setRequirePhoneNumber(body.requirePhoneNumber)
            setRequireAddress(body.requireAddress)
            setRequireBirthdate(body.requireBirthdate)

            onSuccess(body)
            setIsLoading(false)
        }

        return { checkBookingAvailability, isLoading, error }
    }

    function useUnclaimBooking() {
        const { therapistUUID } = useContext(BookingWidgetContext)
        const fingerprint = useFingerprint()

        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)

        async function unclaimBooking(event: ParsedBookableEvent) {
            setIsLoading(true)

            const response = await fetch(`${apiUrl}/events/${therapistUUID}/unclaim`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    fingerprint,
                    start: event.start,
                    end: event.end,
                    eventRuleId: event.ruleId,
                    eventCategoryId: event.categoryId
                })
            })

            if (!response.ok) {
                const body = await response.json()
                setError(resolveErrorMessage(body))
                setIsLoading(false)
                return
            }

            setIsLoading(false)
        }

        return { unclaimBooking, isLoading, error }
    }

    function usePaymentConfiguration() {
        const { therapistUUID } = useContext(BookingWidgetContext)

        const fetched = useRef(false)
        const [paymentConfiguration, setPaymentConfiguration] = useState<PaymentConfiguration | undefined>(undefined)
        const [showLoading, setShowLoading] = useState(true)
        const [error, setError] = useState<string | null>(null)

        useEffect(() => {
            if (!fetched.current) {
                fetched.current = true
                fetchPaymentConfiguration()
            }
        }, [fetched])

        async function fetchPaymentConfiguration() {
            const response = await fetch(`${apiUrl}/events/${therapistUUID}/payment-configuration`)

            setShowLoading(false)

            const body = await response.json()

            if (!response.ok) {
                setError(resolveErrorMessage(body))
                return
            }

            setPaymentConfiguration({ ...body })
        }

        return { paymentConfiguration, showLoading, error, mutate: fetchPaymentConfiguration }
    }

    function useGeneratePayPalClientToken() {
        const { therapistUUID } = useContext(BookingWidgetContext)

        const fetched = useRef(false)
        const [data, setData] = useState<{ token: string } | undefined>(undefined)
        const [showLoading, setShowLoading] = useState(true)
        const [error, setError] = useState<string | null>(null)

        useEffect(() => {
            if (!fetched.current) {
                fetched.current = true
                generatePayPalClientToken()
            }
        }, [fetched])

        async function generatePayPalClientToken() {
            const response = await fetch(`${apiUrl}/events/${therapistUUID}/paypal-client-token`)

            setShowLoading(false)

            const body = await response.json()

            if (!response.ok) {
                setError(resolveErrorMessage(body))
                return
            }

            setData({ token: body.token })
        }

        return { data, showLoading, error, mutate: generatePayPalClientToken }
    }

    function useCreateCheckoutOrder() {
        const { therapistUUID } = useContext(BookingWidgetContext)

        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)

        async function createCheckoutOrder({
            event,
            firstName,
            lastName,
            email,
            address
        }: {
            event: ParsedBookableEvent
            firstName: string
            lastName: string
            email: string
            address?: {
                streetAndNumber: string
                city: { zip: string; name: string }
            }
        }) {
            setIsLoading(true)
            setError(null)

            const response = await fetch(`${apiUrl}/events/${therapistUUID}/create-checkout-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    eventCategoryId: event.categoryId,
                    start: event.startDatetime.toISO(),
                    firstName,
                    lastName,
                    email,
                    returnUrl: buildBookingWidgetPaymentReturnUrl(),
                    ...(address ? { address } : {})
                })
            })

            if (!response.ok) {
                const body = await response.json()
                const message = resolveErrorMessage(body)

                setError(message)
                setIsLoading(false)

                throw new Error(message)
            }

            setIsLoading(false)

            // Handle potentially empty response
            let responseBody: any = {}
            const contentType = response.headers.get('content-type')
            if (contentType?.includes('application/json')) {
                const text = await response.text()
                if (text) {
                    responseBody = JSON.parse(text)
                }
            }

            return responseBody
        }

        return { createCheckoutOrder, isLoading, error }
    }

    function useCreateCheckoutSession() {
        const { therapistUUID } = useContext(BookingWidgetContext)

        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)

        const createCheckoutSession = useCallback(
            async ({
                event,
                firstName,
                lastName,
                email,
                address,
                onlinePaymentMethod
            }: {
                event: ParsedBookableEvent
                firstName: string
                lastName: string
                email: string
                onlinePaymentMethod?: OnlinePaymentMethods[number]
                address?: {
                    streetAndNumber: string
                    city: { zip: string; name: string }
                }
            }) => {
                setIsLoading(true)
                setError(null)

                const response = await fetch(`${apiUrl}/events/${therapistUUID}/create-checkout-session`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        eventCategoryId: event.categoryId,
                        start: event.startDatetime.toISO(),
                        firstName,
                        lastName,
                        email,
                        onlinePaymentMethod,
                        returnUrl: buildStripeCheckoutSessionReturnUrl(),
                        ...(address ? { address } : {})
                    })
                })

                if (!response.ok) {
                    const body = await response.json()
                    const message = resolveErrorMessage(body)

                    setError(message)
                    setIsLoading(false)

                    throw new Error(message)
                }

                setIsLoading(false)

                const responseBody = (await response.json()) as { id: string; clientSecret: string; returnUrl?: string }
                return responseBody
            },
            [therapistUUID]
        )

        return { createCheckoutSession, isLoading, error }
    }

    function useBookEvent() {
        const { therapistUUID, publicBookingSiteId } = useContext(BookingWidgetContext)
        const { setIsEventAlreadyBookedDialogOpen } = useContext(BookingContext)
        const fingerprint = useFingerprint()

        const [isLoading, setIsLoading] = useState(false)
        const [error, setError] = useState<string | null>(null)

        async function bookEvent({
            event,
            selectedLocation,
            ...rest
        }: {
            event: ParsedBookableEvent
            selectedLocation: EventLocationSelection
        } & Record<string, any>): Promise<Booking> {
            setIsLoading(true)

            const authenticatedUrl = `${apiUrl}/events/${therapistUUID}/book-as-client`
            const anonymousUrl = `${apiUrl}/events/${therapistUUID}/book`
            const url = session ? authenticatedUrl : anonymousUrl

            const authenticatedBody = {
                fingerprint,
                start: event.start,
                end: event.end,
                eventRuleId: event.ruleId,
                eventCategoryId: event.categoryId,
                locationSelection: selectedLocation,
                paymentType: rest.paymentType,
                orderId: rest.orderId,
                useCredits: rest.useCredits,
                address: rest.address
            }

            const anonymousBody = {
                fingerprint,
                start: event.start,
                end: event.end,
                eventRuleId: event.ruleId,
                eventCategoryId: event.categoryId,
                locationSelection: selectedLocation,
                payment: rest.paymentType
                    ? {
                          type: rest.paymentType,
                          orderId: rest.orderId
                      }
                    : undefined,
                ...(publicBookingSiteId ? { publicBookingSiteId } : {}),
                ...rest
            }

            const headers: Record<string, string> = { 'Content-Type': 'application/json' }
            if (session) {
                headers.Authorization = `Bearer ${session.accessToken}`
            }

            const response = await fetch(url, {
                method: 'POST',
                headers,
                credentials: session ? 'omit' : undefined,
                body: JSON.stringify(session ? authenticatedBody : anonymousBody)
            })

            if (!response.ok) {
                const body = await response.json()
                const message = resolveErrorMessage(body)

                if (response.status === 400 && isEventAlreadyBookedError(body)) {
                    setIsEventAlreadyBookedDialogOpen(true)
                    throw new Error(body.type)
                }

                setError(message)
                setIsLoading(false)
                throw new Error(message)
            }

            setIsLoading(false)

            // Handle potentially empty response
            let responseBody: any = {}
            const contentType = response.headers.get('content-type')
            if (contentType?.includes('application/json')) {
                const text = await response.text()
                if (text) {
                    responseBody = JSON.parse(text)
                }
            }

            return {
                id: responseBody?.id || Date.now(),
                createdAt: responseBody?.createdAt || new Date().toISOString(),
                locationSelection: selectedLocation,
                ics: responseBody?.ics || '',
                isConfirmed: responseBody?.isConfirmed ?? false,
                isOutage: responseBody?.isOutage || false,
                ...responseBody
            }
        }

        return { bookEvent, isLoading, error }
    }

    return {
        useParallelWidgetBootstrap,
        useBookableEvents,
        useCheckBookingAvailability,
        useUnclaimBooking,
        usePaymentConfiguration,
        useGeneratePayPalClientToken,
        useCreateCheckoutOrder,
        useCreateCheckoutSession,
        useBookEvent
    }
}

// Utilities

export function useFingerprint() {
    const [fingerprint, setFingerprint] = useState<string>(() => {
        if (typeof window === 'undefined') {
            return ''
        }
        return sessionStorage.getItem('tebuto-fingerprint') || window.crypto.randomUUID()
    })

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }
        const storedFingerprint = sessionStorage.getItem('tebuto-fingerprint')
        if (storedFingerprint) {
            setFingerprint((current) => (current === storedFingerprint ? current : storedFingerprint))
            return
        }
        setFingerprint((current) => {
            const next = current || window.crypto.randomUUID()
            sessionStorage.setItem('tebuto-fingerprint', next)
            return next
        })
    }, [])

    return fingerprint
}
